import { PatientEntityType } from "@/lib/supabase/supabaseTypes";

/**
 * Two folded variants per value: umlauts expanded ("mueller") and accents
 * plainly stripped ("muller"), both lowercase. Matching a token against both
 * lets "müller", "mueller" AND "muller" all find "Müller".
 */
function folds(value: string): [string, string] {
	const base = value.toLowerCase().replace(/ß/g, "ss");
	const expanded = base
		.replace(/ä/g, "ae")
		.replace(/ö/g, "oe")
		.replace(/ü/g, "ue");
	const strip = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "");
	return [strip(expanded), strip(base)];
}

function foldedIncludes(
	field: [string, string],
	token: [string, string]
): boolean {
	return field[0].includes(token[0]) || field[1].includes(token[1]);
}

/** "1980-05-15" → "15.05.1980"; date columns arrive as plain ISO strings. */
function toGermanDate(isoDate: string): string {
	const [year, month, day] = isoDate.split("-");
	return year && month && day ? `${day}.${month}.${year}` : "";
}

/**
 * Client-side patient search over an already-loaded list: every whitespace
 * token must match SOME field (partial name, code substring, or birth date —
 * German "15.05.1980"/"15.5.1980" as well as ISO/partial like "1980").
 * Deliberately not anchored to word starts, so inner fragments match too.
 */
export function filterPatientsBySearch(
	patients: PatientEntityType[],
	query: string
): PatientEntityType[] {
	const rawTokens = query.trim().split(/\s+/).filter(Boolean);
	if (rawTokens.length === 0) return [];
	const tokens = rawTokens.map((t) => ({ raw: t, folded: folds(t) }));

	return patients.filter((patient) => {
		const name = folds(
			`${patient.first_name ?? ""} ${patient.last_name ?? ""}`
		);
		const code = folds(patient.code);
		const iso = patient.birth_date ?? "";
		// Zero-padded and unpadded variants so "15.5.1980" matches too.
		const deDate = iso ? toGermanDate(iso) : "";
		const deDateShort = deDate.replace(/\b0(\d)/g, "$1");

		return tokens.every(
			(token) =>
				foldedIncludes(name, token.folded) ||
				foldedIncludes(code, token.folded) ||
				(/\d/.test(token.raw) &&
					(iso.includes(token.raw) ||
						deDate.includes(token.raw) ||
						deDateShort.includes(token.raw)))
		);
	});
}
